import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, TreeStructure, FloppyDisk, Info, CaretDown, CaretUp, Warning, MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg } from '@/auth/AuthContext';
import { mfgApi } from './mfgApi';
import ItemPickerField from './ItemPickerField';
import EquipmentPickerDialog from './EquipmentPickerDialog';
import { yen, metaFor, unitCostOf } from './bom/bomUtils';
import { BomComponentRow } from './bom/BomComponentRow';
import { BomProcessFields } from './bom/BomProcessFields';
import { BomExplosion } from './bom/BomExplosion';

export default function Bom() {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const [items, setItems] = useState([]);
  const [itemId, setItemId] = useState('');
  const [components, setComponents] = useState([]);
  const [workCenters, setWorkCenters] = useState([]);
  const [workCenterId, setWorkCenterId] = useState('');
  const [equipment, setEquipment] = useState([]);
  const [equipmentIds, setEquipmentIds] = useState([]);
  const [bomEquipment, setBomEquipment] = useState([]);   // GET /bom で解決済みの設備（マスター外のフォールバック）
  const [eqPickerOpen, setEqPickerOpen] = useState(false);
  const [standardHours, setStandardHours] = useState('');
  const [actualAvg, setActualAvg] = useState(null);
  const [actualN, setActualN] = useState(0);
  const [hourlyRate, setHourlyRate] = useState(null);
  const [explodeQty, setExplodeQty] = useState(1);
  const [explosion, setExplosion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [filter, setFilter] = useState('');
  const [confirmLeave, setConfirmLeave] = useState(null); // { run: fn } — pending navigation after confirm
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showGuide, setShowGuide] = useState(() => {
    try { return localStorage.getItem('mfg_bom_guide_hidden') !== '1'; } catch { return true; }
  });
  const toggleGuide = () => setShowGuide((v) => {
    const nv = !v;
    try { localStorage.setItem('mfg_bom_guide_hidden', nv ? '0' : '1'); } catch { /* noop */ }
    return nv;
  });

  useEffect(() => { mfgApi.items().then(setItems).catch(() => {}); }, []);
  useEffect(() => { mfgApi.workCenters().then((d) => setWorkCenters(Array.isArray(d) ? d : [])).catch(() => {}); }, []);
  useEffect(() => { mfgApi.equipment().then((d) => setEquipment(Array.isArray(d) ? d : [])).catch(() => {}); }, []);

  const loadBom = useCallback((id) => {
    if (!id) return;
    mfgApi.bom(id).then((d) => { setComponents(d.components || []); setWorkCenterId(d.work_center_id || ''); setEquipmentIds(d.equipment_ids || []); setBomEquipment(d.equipment || []); setStandardHours(d.standard_hours ?? ''); setActualAvg(d.actual_avg_hours ?? null); setActualN(d.actual_sample_count || 0); setHourlyRate(d.hourly_rate ?? null); setDirty(false); }).catch(() => {});
  }, []);
  const loadExplosion = useCallback((id, qty) => {
    if (!id) return;
    mfgApi.explodeBom(id, qty || 1).then(setExplosion).catch(() => {});
  }, []);

  const doPick = useCallback((id) => { setItemId(id); loadBom(id); loadExplosion(id, explodeQty); }, [loadBom, loadExplosion, explodeQty]);
  // 未保存の変更があるまま別の品目に切り替えようとしたら確認
  const pick = (id) => {
    if (dirty && id !== itemId) { setConfirmLeave({ run: () => doPick(id) }); return; }
    doPick(id);
  };

  // 部品表一覧からの遷移 (?item=<id>) — 品目を自動選択
  useEffect(() => {
    const qp = searchParams.get('item');
    if (qp && qp !== itemId) {
      doPick(qp);
      searchParams.delete('item');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, itemId, doPick, setSearchParams]);

  const addRow = () => { setComponents((p) => [...p, { _key: Math.random().toString(36).slice(2), component_item_id: '', qty_per: 1, uom: '', notes: '' }]); setDirty(true); };
  const setRow = (i, k, v) => { setComponents((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r))); setDirty(true); };
  // 構成部品を選ぶと、単位はその品目の登録単位を自動セット（手入力しない）。既に追加済みの部品は重複禁止。
  const pickComponent = (i, id, item) => {
    if (id && components.some((r, idx) => idx !== i && r.component_item_id === id)) {
      toast.error(t('mfg.bom.dupComponent')); return;
    }
    setComponents((p) => p.map((r, idx) => (idx === i ? {
      ...r, component_item_id: id, uom: item?.uom || '',
      name: item?.name, sku: item?.sku, model_no: item?.model_no, maker_pn: item?.maker_pn,
      maker: item?.maker, ref_designators: item?.ref_designators, on_hand: item?.on_hand,
      is_subcontracted: item?.processing_type === 'subcontracted',
      unit_cost: (item?.processing_type === 'subcontracted' && item?.subcontract_unit_price != null) ? item.subcontract_unit_price : (item?.cost || 0),
    } : r)));
    setDirty(true);
  };
  const removeRow = (i) => { setComponents((p) => p.filter((_, idx) => idx !== i)); setDirty(true); };

  const save = async () => {
    const clean = components.filter((c) => c.component_item_id && Number(c.qty_per) > 0)
      .map((c) => ({ component_item_id: c.component_item_id, qty_per: Number(c.qty_per), uom: c.uom || null, notes: c.notes || null }));
    setBusy(true);
    try {
      await mfgApi.setBom(itemId, { components: clean, work_center_id: workCenterId || null, standard_hours: standardHours === '' ? null : Number(standardHours), equipment_ids: equipmentIds });
      setDirty(false);
      toast.success(t('mfg.common.saved')); loadBom(itemId); loadExplosion(itemId, explodeQty);
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
    finally { setBusy(false); }
  };

  // 未保存ガード: ブラウザの再読込・タブ閉じ
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // 未保存ガード: アプリ内リンク（サイドバー/アプリ切替など）のクリックを捕捉
  useEffect(() => {
    if (!dirty) return undefined;
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('#') || a.target === '_blank' || a.hasAttribute('download')) return;
      if (href === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      setConfirmLeave({ run: () => navigate(href) });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [dirty, navigate]);

  const proceedLeave = () => { const run = confirmLeave?.run; setConfirmLeave(null); setDirty(false); run && run(); };

  const fq = filter.trim().toLowerCase();
  const visibleRows = components.map((c, i) => ({ c, i })).filter(({ c }) => {
    if (!fq || !c.component_item_id) return true;
    const m = metaFor(c, items);
    return [m.name, m.sku, m.model_no, m.maker_pn, m.maker, m.ref_designators, c.notes]
      .some((v) => String(v || '').toLowerCase().includes(fq));
  });
  const editorSubtotal = components.reduce((s, c) => (c.component_item_id ? s + unitCostOf(metaFor(c, items)) * Number(c.qty_per || 0) : s), 0);

  return (
    <div className="space-y-5 page-enter" data-testid="mfg-bom">
      <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('mfg.bom.title')}</h1>

      {/* 使い方ガイド (quick start) */}
      <div className="rounded-xl border border-orange-200 bg-orange-50/50 overflow-hidden" data-testid="bom-guide">
        <button type="button" onClick={toggleGuide} data-testid="bom-guide-toggle"
          className="flex w-full items-center gap-2 px-4 py-2.5 text-left">
          <Info size={16} weight="duotone" className="text-orange-500 shrink-0" />
          <span className="text-sm font-semibold text-orange-800">{t('mfg.bom.guideTitle')}</span>
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-orange-600">
            {showGuide ? t('mfg.bom.guideHide') : t('mfg.bom.guideShow')}
            {showGuide ? <CaretUp size={12} /> : <CaretDown size={12} />}
          </span>
        </button>
        {showGuide && (
          <ul className="space-y-1.5 px-4 pb-3 pt-0.5 text-xs leading-relaxed text-orange-900/80" data-testid="bom-guide-body">
            <li>{t('mfg.bom.guide1')}</li>
            <li>{t('mfg.bom.guide2')}</li>
            <li>{t('mfg.bom.guide3')}</li>
            <li>{t('mfg.bom.guide4')}</li>
          </ul>
        )}
      </div>

      <div className="max-w-md">
        <Label className="text-xs font-semibold text-zinc-600">{t('mfg.bom.selectItem')}</Label>
        <div className="mt-1">
          <ItemPickerField testid="bom-item" value={itemId} initialItems={items} onChange={(id) => pick(id)}
            placeholderKey="mfg.bom.selectItem" allowAllDepartments />
        </div>
      </div>

      {!itemId ? (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-12 text-center">
          <TreeStructure size={44} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-500" data-testid="bom-pick-prompt">{t('mfg.bom.pickPrompt')}</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Editor */}
          <section className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
              <h2 className="text-sm font-semibold text-zinc-800">{t('mfg.bom.components')}</h2>
              {canWrite && <Button size="sm" variant="ghost" data-testid="bom-add-component" onClick={addRow} className="h-7 text-orange-600"><Plus size={14} className="mr-1" /> {t('mfg.bom.addComponent')}</Button>}
            </div>
            <BomProcessFields
              canWrite={canWrite}
              workCenters={workCenters}
              workCenterId={workCenterId}
              setWorkCenterId={setWorkCenterId}
              equipment={equipment}
              equipmentIds={equipmentIds}
              setEquipmentIds={setEquipmentIds}
              bomEquipment={bomEquipment}
              onOpenEqPicker={() => setEqPickerOpen(true)}
              standardHours={standardHours}
              setStandardHours={setStandardHours}
              actualAvg={actualAvg}
              actualN={actualN}
              hourlyRate={hourlyRate}
              setDirty={setDirty}
            />
            <div className="p-4 space-y-2">
              {components.length === 0 && <p className="text-center text-sm text-zinc-400 py-6" data-testid="bom-empty">{t('mfg.bom.empty')}</p>}
              {components.length > 3 && (
                <div className="flex items-center gap-2" data-testid="bom-filter-bar">
                  <div className="relative flex-1">
                    <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                    <input value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="bom-filter-input"
                      placeholder={t('mfg.bom.filterPh')}
                      className="h-8 w-full rounded-md border border-zinc-200 bg-zinc-50/60 pl-8 pr-2 text-xs text-zinc-700 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:bg-white" />
                  </div>
                  {fq && <span className="text-[11px] text-zinc-400 shrink-0 tabular-nums" data-testid="bom-filter-count">{t('mfg.bom.filterCount', { shown: visibleRows.length, total: components.length })}</span>}
                </div>
              )}
              {fq && visibleRows.length === 0 && components.length > 0 && (
                <p className="text-center text-sm text-zinc-400 py-6" data-testid="bom-filter-nomatch">{t('mfg.bom.noFilterMatch')}</p>
              )}
              {visibleRows.map(({ c, i }) => (
                <BomComponentRow
                  key={c._key || c.component_item_id || i}
                  c={c}
                  i={i}
                  items={items}
                  itemId={itemId}
                  excludeIds={components.filter((_, idx) => idx !== i).map((r) => r.component_item_id)}
                  canWrite={canWrite}
                  onPick={pickComponent}
                  onSetRow={setRow}
                  onRemove={removeRow}
                />
              ))}
            </div>
            <div className="px-4 py-3 border-t border-zinc-200 flex items-center justify-between gap-3">
              <span className="text-xs text-zinc-500" data-testid="bom-editor-subtotal">
                {t('mfg.bom.componentsSubtotal')}: <b className="text-emerald-700 tabular-nums">{yen(editorSubtotal)}</b>
              </span>
              {canWrite && (
                <Button data-testid="bom-save-btn" onClick={save} disabled={busy} className="h-10 bg-teal-700 hover:bg-teal-800"><FloppyDisk size={16} className="mr-1.5" /> {busy ? t('mfg.common.saving') : t('mfg.bom.save')}</Button>
              )}
            </div>
          </section>

          <BomExplosion
            explosion={explosion}
            explodeQty={explodeQty}
            onQtyChange={(v) => { setExplodeQty(v); loadExplosion(itemId, v); }}
          />
        </div>
      )}

      {/* 設備検索パレット */}
      <EquipmentPickerDialog open={eqPickerOpen} onOpenChange={setEqPickerOpen}
        equipment={equipment} excludeIds={equipmentIds}
        onPick={(e) => { setEquipmentIds((p) => [...p, e.id]); setDirty(true); }} />

      {/* 未保存の変更ガード */}
      <Dialog open={!!confirmLeave} onOpenChange={(o) => { if (!o) setConfirmLeave(null); }}>
        <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="bom-unsaved-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-zinc-900">
              <Warning size={18} weight="fill" className="text-amber-500" />{t('mfg.bom.unsavedTitle')}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">{t('mfg.bom.unsavedDesc')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" data-testid="bom-unsaved-stay" onClick={() => setConfirmLeave(null)}>{t('mfg.bom.stay')}</Button>
            <Button variant="destructive" data-testid="bom-unsaved-leave" onClick={proceedLeave}>{t('mfg.bom.leaveDiscard')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
